import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ProfileDashboard from './ProfileDashboard';
import '../styles/SalesPipeline.css';
import { FaTrash, FaEdit, FaPlus } from 'react-icons/fa';
import { graphqlRequest } from '../utils/graphqlRequest';
import { formatAUD } from '../utils/formatCurrency';
import { patchAdminSearch } from '../utils/adminDashboardNav';

function findCompanyInPipeline(pipeline, bookingId) {
  const id = String(bookingId || '');
  if (!id) return null;
  for (const stage of Object.values(pipeline || {})) {
    const company = (stage.companies || []).find(
      (item) => String(item.id) === id,
    );
    if (company) {
      return {
        ...company,
        stageId: stage.id,
        stageIdName: stage.title,
      };
    }
  }
  return null;
}

export default function SalesPipeline({ pipelineId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [movingLeadId, setMovingLeadId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Lead states
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedLead, setSelectedLead] = useState('');

  // Stage editing modal
  const [showStageModal, setShowStageModal] = useState(false);
  const [editStage, setEditStage] = useState(null);
  const [newStageName, setNewStageName] = useState('');
  const [newStageOrder, setNewStageOrder] = useState('');

  // Add Stage Modal
  const [showAddStageModal, setShowAddStageModal] = useState(false);
  const [addStageName, setAddStageName] = useState('');
  const [addStageOrder, setAddStageOrder] = useState('');


  const viewingLeadRef = useRef(false);
  viewingLeadRef.current = Boolean(selectedCompany);
  const selectedCompanyRef = useRef(selectedCompany);
  selectedCompanyRef.current = selectedCompany;
  const closingLeadRef = useRef(false);
  const draggingRef = useRef(false);
  const pipelineReadyRef = useRef(false);

  // Load pipeline
  const fetchPipeline = async (background = false) => {
    if (!background) {
      setLoading(true);
      setLoadError('');
    }

    const query = `
      query {
        findAllStagesOfGivenPipeline(id: "${pipelineId}") {
          id
          name
          order
          booking {
            id
            fullName
            email
            phone
            address
            postcode
            message
            status
            estimatedPRice
          }
        }
      }
    `;

    try {
      const data = await graphqlRequest(query);
      const stages = data?.findAllStagesOfGivenPipeline ?? [];

      const formatted = stages.reduce((acc, stage) => {
        const bookings = stage?.booking ?? [];
        acc[stage.id] = {
          id: stage.id,
          title: stage.name,
          order: stage.order,
          companies: bookings.map((b) => ({
            id: b.id,
            clientName: b.fullName,
            contactNumber: b.phone,
            contactEmail: b.email,
            address: b.address,
            postcode: b.postcode,
            message: b.message,
            status: b.status,
            estimatedPRice: b.estimatedPRice,
            stageId: stage.id,
          })),
        };
        return acc;
      }, {});

      setPipeline(formatted);
      pipelineReadyRef.current = true;
    } catch (e) {
      console.error('Error loading pipeline', e);
      pipelineReadyRef.current = true;
      if (!background) {
        setLoadError(e?.message || 'Pipeline could not be loaded.');
      }
    }

    if (!background) setLoading(false);
  };

  useEffect(() => {
    fetchPipeline();
    const refresh = () => {
      if (viewingLeadRef.current || draggingRef.current) return;
      fetchPipeline(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 60000);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId]);

  const urlBookingId = new URLSearchParams(location.search).get('bookingId');

  useEffect(() => {
    if (!urlBookingId) {
      if (closingLeadRef.current) {
        closingLeadRef.current = false;
        setSelectedCompany(null);
        return;
      }
      const openLead = selectedCompanyRef.current;
      if (openLead?.id) {
        navigate(
          {
            pathname: location.pathname || '/',
            search: patchAdminSearch(location.search, {
              bookingId: openLead.id,
            }),
          },
          { replace: true },
        );
      }
      return;
    }
    const found = findCompanyInPipeline(pipeline, urlBookingId);
    if (found) {
      setSelectedCompany((prev) =>
        prev && prev.id === found.id && prev.stageId === found.stageId
          ? prev
          : found,
      );
      return;
    }
    const openLead = selectedCompanyRef.current;
    if (openLead && String(openLead.id) === String(urlBookingId)) {
      return;
    }
    if (pipelineReadyRef.current && !loading) {
      setSelectedCompany(null);
      navigate(
        {
          pathname: location.pathname || '/',
          search: patchAdminSearch(location.search, {}, [
            'bookingId',
            'tab',
            'quote',
            'invoice',
            'project',
          ]),
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline, urlBookingId, loading, loadError, location.pathname, navigate]);

  const openOpportunity = (company) => {
    setSelectedCompany(company);
    navigate({
      pathname: '/',
      search: patchAdminSearch(location.search, {
        page: 'sales-pipeline',
        pipelineId,
        bookingId: company.id,
      }),
    });
  };

  const closeOpportunity = () => {
    closingLeadRef.current = true;
    setSelectedCompany(null);
    navigate(
      {
        pathname: '/',
        search: patchAdminSearch(location.search, {}, [
          'bookingId',
          'tab',
          'quote',
          'invoice',
          'project',
        ]),
      },
      { replace: true },
    );
    fetchPipeline(true);
  };

  const orderedStages = useMemo(
    () => Object.values(pipeline).sort((a, b) => Number(a.order) - Number(b.order)),
    [pipeline],
  );

  const boardSummary = useMemo(() => {
    const deals = orderedStages.flatMap((stage) => stage.companies || []);
    return {
      stages: orderedStages.length,
      deals: deals.length,
      value: deals.reduce(
        (sum, deal) => sum + (Number(deal.estimatedPRice) || 0),
        0,
      ),
    };
  }, [orderedStages]);

  const matchesSearch = (company) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;
    return [
      company.clientName,
      company.contactEmail,
      company.contactNumber,
      company.address,
      company.message,
    ].some((value) => String(value || '').toLowerCase().includes(query));
  };

  // -----------------------------------------------------
  // DRAG & DROP
  // -----------------------------------------------------
  const onDragEnd = async (result) => {
    const { source, destination } = result;

    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const sourceCol = pipeline[source.droppableId];
    const destCol = pipeline[destination.droppableId];
    if (!sourceCol || !destCol) return;

    const previousPipeline = pipeline;

    const sourceItems = [...sourceCol.companies];
    const [moved] = sourceItems.splice(source.index, 1);
    const destItems =
      source.droppableId === destination.droppableId
        ? sourceItems
        : [...destCol.companies];
    destItems.splice(destination.index, 0, moved);

    setPipeline({
      ...pipeline,
      [source.droppableId]: { ...sourceCol, companies: sourceItems },
      [destination.droppableId]: { ...destCol, companies: destItems },
    });
    setMovingLeadId(moved.id);

    const mutation = `
      mutation {
        UpdateItemInsideStage(
          createStageBooking: {
            pipelineId: "${pipelineId}"
            stageId: "${destination.droppableId}"
            bookingId: "${moved.id}"
          }
        ) {
          id
        }
      }
    `;

    try {
      await graphqlRequest(mutation);
      toast.success(`Moved ${moved.clientName || 'lead'} to ${destCol.title}`);
    } catch (err) {
      console.error('Failed to move booking in pipeline:', err);
      setPipeline(previousPipeline);
      toast.error(err?.message || 'Could not move the lead. The board was restored.');
    } finally {
      setMovingLeadId(null);
    }
  };

  // -----------------------------------------------------
  // DELETE BOOKING
  // -----------------------------------------------------
  const handleDeleteBooking = async (stageId, bookingId) => {
    const company = pipeline?.[stageId]?.companies?.find((item) => item.id === bookingId);
    if (!window.confirm(`Remove "${company?.clientName || 'this lead'}" from this stage?`)) {
      return;
    }
    const mutation = `
      mutation {
        removeBookingFromStage(stageId: "${stageId}", bookingId: "${bookingId}") 
      } 
    `;

    try {
      const data = await graphqlRequest(mutation);
      const ok = Boolean(data?.removeBookingFromStage);
      if (!ok) throw new Error('Lead could not be removed from the stage');
      setPipeline((prev) => {
        const stage = prev?.[stageId];
        const companies = Array.isArray(stage?.companies) ? stage.companies : [];
        return {
          ...prev,
          [stageId]: {
            ...stage,
            companies: companies.filter((c) => c?.id !== bookingId),
          },
        };
      });
      toast.success('Lead removed from stage');
    } catch (error) {
      toast.error(error?.message || 'Could not remove lead from stage');
    }
  };

  // -----------------------------------------------------
  // DELETE STAGE
  // -----------------------------------------------------
  const handleDeleteStage = async (stageId) => {
    const stage = pipeline?.[stageId];
    const dealCount = stage?.companies?.length || 0;
    const warning = dealCount
      ? `Delete "${stage?.title}"? It currently contains ${dealCount} deal${dealCount === 1 ? '' : 's'}.`
      : `Delete "${stage?.title || 'this stage'}"?`;
    if (!window.confirm(`${warning}\n\nThis cannot be undone.`)) return;
    const query = `
      mutation {
        deleteStage(stageId: "${stageId}")
      }
    `;
    try {
      const data = await graphqlRequest(query);
      if (!data?.deleteStage) throw new Error('Stage could not be deleted');
      setPipeline((prev) => {
        const temp = { ...prev };
        delete temp[stageId];
        return temp;
      });
      toast.success(`Deleted "${stage?.title || 'stage'}"`);
    } catch (error) {
      toast.error(error?.message || 'Could not delete stage');
    }
  };

  // -----------------------------------------------------
  // ADD LEAD
  // -----------------------------------------------------
  const handleAddLead = async (stageId) => {
    setSelectedStage(stageId);
    setShowLeadModal(true);

    const query = `
      query FindLeadsForPipeline($page: Float, $limit: Float) {
        findAllLeads(page: $page, limit: $limit, includeSignUrls: false) {
          data {
            id
            fullName
            email
            phone
            address
            message
            status
            estimatedPRice
          }
        }
      }
    `;

    const variables = { page: 1, limit: 500 };

    const data = await graphqlRequest(query, variables);
    const rows = data?.findAllLeads?.data ?? [];
    const alreadyInPipeline = new Set(
      Object.values(pipeline).flatMap((col) =>
        (col.companies || []).map((c) => c.id),
      ),
    );
    // Sales pipelines accept qualified leads only. New and qualifying enquiries
    // remain in Lead Management until qualification is complete.
    const eligible = rows.filter((b) => {
      if (!b?.id || alreadyInPipeline.has(b.id)) return false;
      const status = String(b.status ?? '').toUpperCase();
      return status.startsWith('APPROVE');
    });
    setAvailableLeads(eligible);
    if (eligible.length === 0) {
      toast.info('No qualified leads are available. Qualify the lead in Lead Management first.');
    }
  };

  const handleConfirmAddLead = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead.');
      return;
    }
    const mutation = `
      mutation {
        insertItemInsideStage(
          CreateStageBooking: {
            stageId: "${selectedStage}"
            bookingId: "${selectedLead}"
            pipelineId: "${pipelineId}"
          }
        ) {
          id
          booking {
            id
            fullName
            phone
            email
            address
            postcode
            message
            status
            estimatedPRice
          }
        }
      }
    `;

    try {
      const data = await graphqlRequest(mutation);
      const stageData = data?.insertItemInsideStage;

      if (stageData?.booking?.length) {
        setPipeline((prev) => ({
          ...prev,
          [stageData.id]: {
            ...prev[stageData.id],
            companies: stageData.booking.map((b) => ({
              id: b.id,
              clientName: b.fullName,
              contactNumber: b.phone,
              contactEmail: b.email,
              address: b.address,
              postcode: b.postcode,
              message: b.message,
              status: b.status,
              estimatedPRice: b.estimatedPRice,
              stageId: stageData.id,
            })),
          },
        }));
      }
      await fetchPipeline();
    } catch (e) {
      console.error('Add lead to stage failed', e);
      toast.error(e?.message || 'Could not add lead to this stage.');
    }

    setShowLeadModal(false);
    setSelectedLead('');
  };

  // -----------------------------------------------------
  // EDIT STAGE
  // -----------------------------------------------------
  const openEditStage = (stage) => {
    setEditStage(stage);
    setNewStageName(stage.title);
    setNewStageOrder(stage.order);
    setShowStageModal(true);
  };

  const handleUpdateStage = async () => {
    const name = String(newStageName || '').trim();
    const order = Number(newStageOrder);

    if (!editStage?.id || !name || Number.isNaN(order)) {
      toast.error('Fill all fields');
      return;
    }

    const mutation = `
      mutation UpdatePipelineStage($input: UpdatePipelineStageInput!) {
        updatePipelineStage(updatePipelineStageInput: $input) {
          id
          name
          order
        }
      }
    `;

    try {
      const data = await graphqlRequest(mutation, {
        input: {
          id: editStage.id,
          name,
          order,
        },
      });
      const updated = data?.updatePipelineStage;
      if (!updated?.id) return;

      setPipeline((prev) => ({
        ...prev,
        [updated.id]: {
          ...prev[updated.id],
          title: updated.name,
          order: updated.order,
        },
      }));

      setShowStageModal(false);
    } catch (e) {
      console.error('Update stage failed', e);
      toast.error(e?.message || 'Could not update stage.');
    }
  };

  // -----------------------------------------------------
  // ADD NEW STAGE
  // -----------------------------------------------------
  const handleCreateStage = async () => {
    const name = String(addStageName || '').trim();
    const order = Number(addStageOrder);

    if (!pipelineId) {
      toast.error('No pipeline selected.');
      return;
    }
    if (!name || Number.isNaN(order)) {
      toast.error('Please enter a stage name and order.');
      return;
    }

    const mutation = `
      mutation CreatePipelineStages($input: CreatePipelineStagesInput!) {
        createPipelineStages(createPipelineStageInput: $input) {
          id
          name
          order
        }
      }
    `;

    try {
      const data = await graphqlRequest(mutation, {
        input: {
          pipelineId,
          stages: [{ name, order }],
        },
      });
      const created = data?.createPipelineStages;
      if (created?.length) {
        // Keep the new stage on the board immediately (same shape as existing stages).
        setPipeline((prev) => {
          const next = { ...prev };
          for (const stage of created) {
            next[stage.id] = {
              id: stage.id,
              title: stage.name,
              order: stage.order,
              companies: [],
            };
          }
          return next;
        });
        setShowAddStageModal(false);
        setAddStageName('');
        setAddStageOrder('');
        await fetchPipeline();
      }
    } catch (e) {
      console.error('Add stage failed', e);
      toast.error(e?.message || 'Could not add stage.');
    }
  };

  // -----------------------------------------------------
  // MAIN UI + PROFILE DASHBOARD
  // -----------------------------------------------------
  if (selectedCompany) {
    return (
      <ProfileDashboard
        company={selectedCompany}
        pipeline={pipeline}
        onBack={closeOpportunity}
      />
    );
  }

  if (urlBookingId) {
    return <p className="pipeline-loading">Loading pipeline...</p>;
  }

  return (
    <div className="sales-report-wrapper">
      <div className="sales-report-box">
        <div className="header-row">
          <div>
            <h2 className="title">Sales Pipeline</h2>
            <p className="pipeline-page-subtitle">
              Progress qualified opportunities from assessment through quotation and close.
            </p>
          </div>
          <div className="pipeline-page-actions">
            <button
              className="add-stage-btn"
              onClick={() => setShowAddStageModal(true)}
            >
              <FaPlus /> Add stage
            </button>
          </div>
        </div>

        <div className="pipeline-board-summary">
          <div><span>Stages</span><strong>{boardSummary.stages}</strong></div>
          <div><span>Open opportunities</span><strong>{boardSummary.deals}</strong></div>
          <div><span>Estimated value</span><strong>{formatAUD(boardSummary.value)}</strong></div>
          <label className="pipeline-board-search">
            <span className="sr-only">Search sales opportunities</span>
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search opportunities…"
            />
          </label>
        </div>

        {searchTerm ? (
          <p className="pipeline-search-hint">
            Dragging is paused while search is active. Clear the search to move opportunities.
          </p>
        ) : null}

        {loading ? (
          <p className="pipeline-loading">Loading pipeline...</p>
        ) : loadError ? (
          <div className="pipeline-error-state">
            <p>{loadError}</p>
            <button type="button" onClick={fetchPipeline}>Try again</button>
          </div>
        ) : orderedStages.length === 0 ? (
          <div className="pipeline-empty-state">
            <p>This pipeline has no stages yet.</p>
            <button type="button" onClick={() => setShowAddStageModal(true)}>
              <FaPlus /> Add the first stage
            </button>
          </div>
        ) : (
          <DragDropContext
            onDragStart={() => {
              draggingRef.current = true;
            }}
            onDragEnd={async (result) => {
              draggingRef.current = false;
              await onDragEnd(result);
            }}
          >
            <div className="pipeline-board">
            <div className="horizontal-scroll">
              {orderedStages.map((stage) => {
                const visibleCompanies = (stage.companies || []).filter(matchesSearch);
                const stageValue = (stage.companies || []).reduce(
                  (sum, company) => sum + (Number(company.estimatedPRice) || 0),
                  0,
                );
                return (
                  <Droppable
                    key={stage.id}
                    droppableId={stage.id}
                    ignoreContainerClipping
                  >
                    {(provided) => (
                      <div className="opportunity-card">
                        <div className="stage-card-top">
                          <div className="stage-header">
                            <p className="opportunity" title={stage.title}>
                              {stage.title}
                            </p>

                            <div className="icon-group">
                              <FaEdit
                                className="edit-icon"
                                onClick={() => openEditStage(stage)}
                              />
                              <FaTrash
                                className="delete-stage-icon"
                                onClick={() => handleDeleteStage(stage.id)}
                              />
                            </div>
                          </div>

                          <p className="deal-count">
                            {(stage.companies || []).length} opportunit
                            {(stage.companies || []).length === 1 ? 'y' : 'ies'}
                            <span>{formatAUD(stageValue)}</span>
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleAddLead(stage.id)}
                          className="add-lead-btn"
                        >
                          + Add qualified lead
                        </button>

                        <div
                          className="stage-cards-scroll"
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {visibleCompanies.map((company, index) => (
                            <Draggable
                              key={company.id}
                              draggableId={company.id.toString()}
                              index={index}
                              isDragDisabled={Boolean(searchTerm.trim()) || movingLeadId === company.id}
                            >
                              {(provided) => (
                                <div
                                  className="company-item"
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <div
                                    className="company-text"
                                    onClick={() =>
                                      openOpportunity({
                                        ...company,
                                        stageId: stage.id,
                                        stageIdName: stage.title,
                                      })
                                    }
                                  >
                                    <span className="deal-name">
                                      {company.clientName}
                                    </span>
                                    {company.estimatedPRice ? (
                                      <span className="deal-amount">
                                        {formatAUD(company.estimatedPRice)}
                                      </span>
                                    ) : null}
                                    {company.contactEmail ? (
                                      <span className="deal-contact">{company.contactEmail}</span>
                                    ) : null}
                                  </div>

                                  <FaTrash
                                    className="delete-booking-btn"
                                    onClick={() =>
                                      handleDeleteBooking(stage.id, company.id)
                                    }
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}

                          {visibleCompanies.length === 0 ? (
                            <p className="stage-empty-copy">
                              {searchTerm ? 'No matching opportunities' : 'No opportunities in this stage'}
                            </p>
                          ) : null}

                          {provided.placeholder}
                        </div>
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>
            </div>
          </DragDropContext>
        )}
      </div>

      {/* ----------------- ADD LEAD MODAL ----------------- */}
      {showLeadModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Add qualified lead</h3>

            <select
              value={selectedLead}
              onChange={(e) => setSelectedLead(e.target.value)}
            >
              <option value="">Select a qualified lead</option>
              {(availableLeads || []).map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {lead.fullName} ({lead.email})
                </option>
              ))}
            </select>

            <div className="modal-actions">
              <button className="add-lead" onClick={handleConfirmAddLead}>
                Add to pipeline
              </button>
              <button onClick={() => setShowLeadModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ----------------- EDIT STAGE MODAL ----------------- */}
      {showStageModal && (
        <div className="modal-overlay-custom">
          <div className="modal-box-custom">
            <h3 className="modal-title">Edit Stage</h3>

            <div className="modal-field">
              <label>Stage Name</label>
              <input
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
              />
            </div>

            <div className="modal-field">
              <label>Order</label>
              <input
                type="number"
                value={newStageOrder}
                onChange={(e) => setNewStageOrder(e.target.value)}
              />
            </div>

            <div className="modal-actions-custom">
              <button
                className="btn-secondary"
                onClick={() => setShowStageModal(false)}
              >
                Cancel
              </button>
              <button className="btn-primary" onClick={handleUpdateStage}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ----------------- ADD STAGE MODAL ----------------- */}
      {showAddStageModal && (
        <div className="modal-overlay-custom">
          <div className="modal-box-custom">
            <h3 className="modal-title">Add New Stage</h3>

            <div className="modal-field">
              <label>Stage Name</label>
              <input
                value={addStageName}
                onChange={(e) => setAddStageName(e.target.value)}
              />
            </div>

            <div className="modal-field">
              <label>Order</label>
              <input
                type="number"
                value={addStageOrder}
                onChange={(e) => setAddStageOrder(e.target.value)}
              />
            </div>

            <div className="modal-actions-custom">
              <button
                className="btn-secondary"
                onClick={() => setShowAddStageModal(false)}
              >
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateStage}>
                Add Stage
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
